/**
 * Google Identity Services (GIS) loader.
 *
 * The website uses GIS's "ID token" mode (not the OAuth 2.0 implicit/code flow). The
 * library renders a button or a One-Tap prompt that, on success, hands us a JWT
 * (`credential`) which we POST to the backend (/candidates/auth/signin/google or /signup/google).
 *
 * Loaded lazily on demand — no eager script tag in index.html, so pages that don't need
 * Google sign-in stay fast.
 */

declare global {
  interface Window {
    google?: GoogleNamespace;
    __qhGoogleLoad?: Promise<GoogleNamespace>;
  }
}

interface GoogleCredentialResponse {
  credential: string;
  select_by?: string;
}

interface GoogleAccountsId {
  initialize: (config: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
  }) => void;
  renderButton: (
    parent: HTMLElement,
    options: {
      type?: 'standard' | 'icon';
      theme?: 'outline' | 'filled_blue' | 'filled_black';
      size?: 'large' | 'medium' | 'small';
      text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
      shape?: 'rectangular' | 'pill' | 'circle' | 'square';
      logo_alignment?: 'left' | 'center';
      width?: number;
    },
  ) => void;
  prompt: () => void;
  cancel: () => void;
  disableAutoSelect: () => void;
}

interface GoogleNamespace {
  accounts: { id: GoogleAccountsId };
}

const GIS_SRC = 'https://accounts.google.com/gsi/client';

export function loadGoogleIdentity(): Promise<GoogleNamespace> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Identity Services requires a browser environment.'));
  }
  if (window.google) return Promise.resolve(window.google);
  if (window.__qhGoogleLoad) return window.__qhGoogleLoad;

  window.__qhGoogleLoad = new Promise<GoogleNamespace>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => {
        if (window.google) resolve(window.google);
        else reject(new Error('Google Identity Services failed to initialize.'));
      });
      existing.addEventListener('error', () => reject(new Error('Failed to load Google Identity Services.')));
      return;
    }
    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google) resolve(window.google);
      else reject(new Error('Google Identity Services failed to initialize.'));
    };
    script.onerror = () => reject(new Error('Failed to load Google Identity Services.'));
    document.head.appendChild(script);
  });
  return window.__qhGoogleLoad;
}

export type { GoogleCredentialResponse, GoogleNamespace };
